import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, Check, X } from "@phosphor-icons/react";

const TABS = [
  { key: "hire_types", label: "Hire Types", desc: "Applied to every allocation line and carried onto dockets.", noun: "hire type" },
  { key: "rate_types", label: "Rate Types", desc: "Charge-out rate basis used on allocations and the weekly summary.", noun: "rate type" },
  { key: "standdown_reasons", label: "Stand Down Reasons", desc: "Reasons a resource is stood down without going off hire.", noun: "reason" },
  { key: "offhire_reasons", label: "Off Hire Reasons", desc: "Reasons an asset is taken off hire.", noun: "reason" },
  { key: "downtime_reasons", label: "Downtime Reasons", desc: "Reasons recorded against downtime hours on field dockets.", noun: "reason" },
  { key: "asset_types", label: "Asset Types", desc: "Categories used to group plant & equipment.", noun: "asset type" },
];

const inp = "h-[42px] px-3 border border-border-control bg-white text-[13.5px] rounded-[9px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
const iconBtn = "w-8 h-8 inline-flex items-center justify-center rounded-[7px] border border-border-control bg-white text-ink-tertiary hover:text-ink-primary hover:bg-surface-panel disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed";

function LookupTable({ meta, onChange }) {
  const { isWriter } = useAuth();
  const [items, setItems] = useState([]);
  const [newVal, setNewVal] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [reassign, setReassign] = useState(null); // { item, to }

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/lookups/${meta.key}`); setItems(data); }
    catch { /* ignore */ }
    setLoading(false);
  }, [meta.key]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange?.(); };
  const add = async () => {
    if (!newVal.trim()) return;
    setError("");
    try { await api.post(`/lookups/${meta.key}`, { value: newVal.trim() }); setNewVal(""); refresh(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveEdit = async (id) => {
    setError("");
    try { await api.put(`/lookups/${meta.key}/${id}`, { value: editVal.trim() }); setEditId(null); refresh(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doDelete = async (id, reassignTo) => {
    setError("");
    const qs = reassignTo ? `?reassign_to=${encodeURIComponent(reassignTo)}` : "";
    try { await api.delete(`/lookups/${meta.key}/${id}${qs}`); setReassign(null); refresh(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = (it) => {
    if (it.in_use > 0) { setReassign({ item: it, to: "" }); return; }
    if (!window.confirm(`Delete "${it.value}"?`)) return;
    doDelete(it.id);
  };

  return (
    <div className="bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden" data-testid="lookup-card">
      <div className="flex items-start justify-between gap-3 px-5 py-3.5 border-b border-divider-inner">
        <div>
          <h2 className="font-heading text-[16px] font-bold tracking-tight text-ink-primary">{meta.label}</h2>
          <p className="text-[12px] text-ink-tertiary mt-0.5">{meta.desc}</p>
        </div>
        <span className="text-[12px] text-ink-tertiary whitespace-nowrap shrink-0" data-testid="lookup-count">{items.length} values</span>
      </div>

      {error && <div className="mx-5 mt-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="lookup-error">{error}</div>}

      {isWriter && (
        <div className="flex gap-2 px-5 py-3.5">
          <input value={newVal} onChange={(e) => setNewVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={`Add a ${meta.noun}…`} data-testid="lookup-new-input" className={inp + " flex-1"} />
          <button onClick={add} data-testid="lookup-add-button"
            className="h-[42px] px-5 bg-equipr-yellow text-equipr-black font-bold text-[12px] uppercase tracking-[0.04em] rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
            <Plus size={15} weight="bold" /> Add
          </button>
        </div>
      )}

      <table className="w-full text-cell">
        <thead>
          <tr className="bg-surface-sunken border-y border-border text-left">
            <th className="px-5 py-2.5 uppercase tracking-[0.1em] text-colhead font-bold text-ink-secondary">Value</th>
            <th className="px-5 py-2.5 uppercase tracking-[0.1em] text-colhead font-medium text-ink-muted text-right w-40">In Use</th>
            <th className="px-5 py-2.5 uppercase tracking-[0.1em] text-colhead font-medium text-ink-muted text-right w-28">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={3} className="px-5 py-8 text-center text-ink-tertiary">Loading…</td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={3} className="px-5 py-8 text-center text-ink-tertiary" data-testid="lookup-empty">No values yet — add the first above.</td></tr>
          ) : items.map((it) => (
            <tr key={it.id} className="border-b border-divider-row hover:bg-accent-wash" data-testid={`lookup-row-${it.id}`}>
              <td className="px-5 py-3">
                {editId === it.id ? (
                  <input value={editVal} autoFocus onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(it.id)}
                    className="h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[7px] w-full max-w-sm focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" data-testid="lookup-edit-input" />
                ) : (
                  <span className="font-semibold text-ink-primary">{it.value}</span>
                )}
              </td>
              <td className="px-5 py-3 text-right text-ink-tertiary">{it.in_use > 0 ? `${it.in_use} record${it.in_use === 1 ? "" : "s"}` : "Not used"}</td>
              <td className="px-5 py-3">
                {isWriter && (
                  <div className="flex items-center justify-end gap-1.5">
                    {editId === it.id ? (
                      <>
                        <button onClick={() => saveEdit(it.id)} className={iconBtn + " text-emerald-600 hover:text-emerald-700"} data-testid="lookup-save-edit"><Check size={15} weight="bold" /></button>
                        <button onClick={() => setEditId(null)} className={iconBtn}><X size={15} /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditId(it.id); setEditVal(it.value); }} className={iconBtn} data-testid={`lookup-edit-${it.id}`}><PencilSimple size={15} /></button>
                        <button onClick={() => remove(it)} title={it.in_use > 0 ? "In use — delete & reassign linked records" : "Delete"} className={iconBtn + " hover:!text-red-600"} data-testid={`lookup-delete-${it.id}`}><Trash size={15} /></button>
                      </>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="px-5 py-3 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary">
        Renaming a value updates every linked record automatically. Deleting a value that's in use asks you to move those records to another value first.
      </div>

      {reassign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(26,26,26,0.42)" }} data-testid="reassign-modal">
          <div className="bg-surface-panel border border-border-warm rounded-[14px] shadow-modal w-full max-w-md overflow-hidden">
            <div className="px-5 py-3.5 border-b border-divider-inner">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">{meta.label}</div>
              <h3 className="font-heading text-[16px] font-bold text-ink-primary mt-0.5">Delete “{reassign.item.value}”</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[13px] text-ink-secondary">
                This {meta.noun} is used by <b>{reassign.item.in_use}</b> record{reassign.item.in_use === 1 ? "" : "s"}.
                Choose a replacement — every linked record will be moved to it, then this value is deleted.
              </p>
              <select value={reassign.to} onChange={(e) => setReassign({ ...reassign, to: e.target.value })}
                className={inp + " w-full"} data-testid="reassign-select">
                <option value="">— Select a replacement —</option>
                {items.filter((x) => x.id !== reassign.item.id).map((x) => (
                  <option key={x.id} value={x.value}>{x.value}</option>
                ))}
              </select>
            </div>
            <div className="px-5 py-3 border-t border-divider-inner bg-surface-panel flex justify-end gap-2">
              <button onClick={() => setReassign(null)} data-testid="reassign-cancel"
                className="h-[38px] px-4 border border-border-control bg-white rounded-[7px] text-[12px] font-bold uppercase tracking-[0.04em] text-ink-secondary hover:bg-surface-panel">Cancel</button>
              <button onClick={() => doDelete(reassign.item.id, reassign.to)} disabled={!reassign.to} data-testid="reassign-confirm"
                className="h-[38px] px-4 rounded-[7px] text-[12px] font-bold uppercase tracking-[0.04em] text-white bg-red-600 border border-red-700 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">Move & Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DataManagement() {
  const [tab, setTab] = useState(TABS[0].key);
  const [counts, setCounts] = useState({});

  const loadCounts = useCallback(async () => {
    const entries = await Promise.all(TABS.map(async (t) => {
      try { const { data } = await api.get(`/lookups/${t.key}`); return [t.key, data.length]; }
      catch { return [t.key, 0]; }
    }));
    setCounts(Object.fromEntries(entries));
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const meta = TABS.find((t) => t.key === tab);
  return (
    <div className="h-full overflow-y-auto bg-surface-page" data-testid="data-management-page">
      <div className="px-6 pt-6 pb-8 max-w-[1180px]">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-muted">System</div>
        <h1 className="font-heading text-[26px] font-bold tracking-tight text-ink-primary mt-1">Data Management</h1>
        <p className="text-[13px] text-ink-tertiary mt-1">The configurable lists behind every dropdown in EQUIPR Civil. Changes apply the next time a form is opened.</p>

        <div className="mt-5 flex gap-5 items-start">
          <aside className="w-[248px] shrink-0">
            <div className="bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
              <div className="px-4 pt-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Lists</div>
              <nav className="pb-2">
                {TABS.map((t) => {
                  const active = tab === t.key;
                  return (
                    <button key={t.key} onClick={() => setTab(t.key)} data-testid={`dm-tab-${t.key}`}
                      className={`w-full flex items-center justify-between pl-4 pr-3 py-2.5 border-l-[3px] text-left transition-colors ${
                        active ? "border-equipr-yellow bg-accent-tint" : "border-transparent hover:bg-surface-panel"}`}>
                      <span className={`text-[13.5px] ${active ? "font-bold text-ink-primary" : "font-medium text-ink-secondary"}`}>{t.label}</span>
                      <span className="text-[12px] text-ink-tertiary tabular-nums">{counts[t.key] ?? ""}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          <section className="flex-1 min-w-0">
            <LookupTable key={tab} meta={meta} onChange={loadCounts} />
          </section>
        </div>
      </div>
    </div>
  );
}
